/**
 * Template Model
 * Enhanced template model with versioning and page structure for the new architecture
 */

import {
  ITEM_TYPES,
  createTemplateItem,
  validateItemValue,
} from "../constants/itemTypes.js";

/**
 * @typedef {Object} Template
 * @property {string} id - Unique template identifier (UUID)
 * @property {string} name - Template name
 * @property {string} description - Template description
 * @property {string} version - Template version (semantic versioning)
 * @property {boolean} isActive - Whether template is active and can be used
 * @property {Array<TemplateItem>} items - Array of template items
 * @property {Object} metadata - Template metadata
 * @property {string} metadata.category - Template category
 * @property {string[]} metadata.tags - Template tags
 * @property {string[]} metadata.countries - Template countries
 * @property {string[]} metadata.languages - Template languages
 * @property {string} createdBy - User ID who created the template
 * @property {string} createdAt - ISO timestamp of creation
 * @property {string} updatedAt - ISO timestamp of last update
 */

/**
 * @typedef {Object} TemplateItem
 * @property {string} id - Unique item identifier
 * @property {string} type - Item type from ITEM_TYPES
 * @property {string} label - Item label/question
 * @property {boolean} required - Whether item is required
 * @property {number} order - Display order
 * @property {Object} config - Item-specific configuration
 * @property {Object}
 */

/**
 * Creates a new template object with default values
 * @param {Object} templateData - Template data to create
 * @param {string} templateData.name - Template name (required)
 * @param {string} [templateData.description] - Template description
 * @param {Array<Object>} [templateData.items] - Template items
 * @param {Object} [templateData.metadata] - Template metadata
 * @returns {Template} New template object
 */
export const createTemplate = (templateData) => {
  const now = new Date().toISOString();
  console.log("TemplateData: ", templateData);

  return {
    id:
      templateData.id ||
      `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name: templateData.name,
    description: templateData.description || "",
    version: templateData.version || 1,
    isActive:
      templateData.isActive !== undefined ? templateData.isActive : true,

    items: (templateData.items || []).map((item, index) => ({
      id:
        item.id ||
        `item_${Date.now()}_${index}_${Math.random()
          .toString(36)
          .substr(2, 9)}`,
      type: item.type || ITEM_TYPES.CHECKBOX,
      description: item.description || "",
      defaultValue: item.defaultValue || {},
      defaultData: item.defaultData || {},
      label: item.label || "",
      required: item.required || false,
      order: item.order !== undefined ? item.order : index,
      config: item.config || {},
    })),

    metadata: {
      // estimatedTimeMinutes: 15,
      category: "general",
      tags: [],
      country: templateData.country,
      languages: [],
      ...templateData.metadata,
    },

    createdBy: templateData.createdBy || null,
    createdAt: now,
    updatedAt: now,
  };
};

/**
 * Validates template data
 * @param {Object} templateData - Template data to validate
 * @returns {Object} Validation result { isValid: boolean, errors: string[] }
 */
export const validateTemplate = (templateData) => {
  const errors = [];
  console.log("TemplateData: ", templateData);

  // Required fields
  if (!templateData.name || templateData.name.trim().length === 0) {
    errors.push("Template name is required");
  }

  if (templateData.name && templateData.name.length > 100) {
    errors.push("Template name must be 100 characters or less");
  }

  if (templateData.description && templateData.description.length > 500) {
    errors.push("Template description must be 500 characters or less");
  }

  // Version validation
  if (templateData.version && !isValidVersion(templateData.version)) {
    errors.push("Version must follow integer versioning (e.g. 1)");
  }

  // Items validation
  if (templateData.items) {
    if (!Array.isArray(templateData.items)) {
      errors.push("Items must be an array");
    } else {
      templateData.items.forEach((item, index) => {
        const itemErrors = validateTemplateItem(item);
        itemErrors.forEach((error) => {
          errors.push(`Item ${index + 1}: ${error}`);
        });
      });
    }
  }

  // Metadata validation
  if (templateData.metadata) {
    if (templateData.metadata.estimatedTimeMinutes !== undefined) {
      if (
        typeof templateData.metadata.estimatedTimeMinutes !== "number" ||
        templateData.metadata.estimatedTimeMinutes < 1 ||
        templateData.metadata.estimatedTimeMinutes > 480
      ) {
        errors.push("Estimated time must be between 1 and 480 minutes");
      }
    }

    if (
      templateData.metadata.tags &&
      !Array.isArray(templateData.metadata.tags)
    ) {
      errors.push("Tags must be an array");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates a template item
 * @param {Object} item - Template item to validate
 * @returns {string[]} Array of validation errors
 */
export const validateTemplateItem = (item) => {
  console.log("item: ", item);
  const errors = [];

  if (!item.type || !Object.values(ITEM_TYPES).includes(item.type)) {
    errors.push("Invalid or missing item type");
  }

  if (!item.label || item.label.trim().length === 0) {
    errors.push("Item label is required");
  }

  if (item.label && item.label.length > 200) {
    errors.push("Item label must be 200 characters or less");
  }

  if (
    item.order !== undefined &&
    (typeof item.order !== "number" || item.order < 0)
  ) {
    errors.push("Item order must be a non-negative number");
  }

  return errors;
};

/**
 * Updates template data with validation
 * @param {Template} template - Existing template object
 * @param {Object} updates - Updates to apply
 * @returns {Template} Updated template object
 */
export const updateTemplate = (template, updates) => {
  const updatedTemplate = {
    ...template,
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  // Handle version increment if items changed
  if (
    updates.items &&
    JSON.stringify(updates.items) !== JSON.stringify(template.items)
  ) {
    // updatedTemplate.version = incrementVersion(template.version);
  }

  // Merge metadata
  if (updates.metadata) {
    updatedTemplate.metadata = { ...template.metadata, ...updates.metadata };
  }

  // Re-order items if needed
  if (updates.items) {
    updatedTemplate.items = updates.items.map((item, index) => ({
      ...item,
      order: item.order !== undefined ? item.order : index,
    }));
  }

  return updatedTemplate;
};

/**
 * Adds an item to a template
 * @param {Template} template - Template to add item to
 * @param {Object} itemData - Item data to add
 * @returns {Template} Updated template with new item
 */
export const addTemplateItem = (template, itemData) => {
  const newItem = {
    id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type: itemData.type || ITEM_TYPES.CHECKBOX,
    label: itemData.label || "",
    required: itemData.required || false,
    order:
      itemData.order !== undefined ? itemData.order : template.items.length,
    config: itemData.config || {},
  };

  const updatedItems = [...template.items, newItem].sort(
    (a, b) => a.order - b.order
  );

  return updateTemplate(template, { items: updatedItems });
};

/**
 * Removes an item from a template
 * @param {Template} template - Template to remove item from
 * @param {string} itemId - ID of item to remove
 * @returns {Template} Updated template without the item
 */
export const removeTemplateItem = (template, itemId) => {
  const updatedItems = template.items.filter((item) => item.id !== itemId);
  return updateTemplate(template, { items: updatedItems });
};

/**
 * Reorders template items
 * @param {Template} template - Template to reorder items in
 * @param {string} itemId - ID of item to move
 * @param {number} newOrder - New order position
 * @returns {Template} Updated template with reordered items
 */
export const reorderTemplateItem = (template, itemId, newOrder) => {
  const updatedItems = template.items
    .map((item) => {
      if (item.id === itemId) {
        return { ...item, order: newOrder };
      }
      return item;
    })
    .sort((a, b) => a.order - b.order);

  return updateTemplate(template, { items: updatedItems });
};

/**
 * Creates a copy of a template with new ID and version
 * @param {Template} template - Template to duplicate
 * @param {Object} [overrides] - Properties to override in the copy
 * @returns {Template} Duplicated template
 */
export const duplicateTemplate = (template, overrides = {}) => {
  const now = new Date().toISOString();

  return {
    ...template,
    id: `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name: `${template.name} (Copy)`,
    version: "1.0.0",
    items: template.items.map((item) => ({
      ...item,
      id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    })),
    createdAt: now,
    updatedAt: now,
    ...overrides,
  };
};

/**
 * Gets template display name
 * @param {Template} template - Template object
 * @returns {string} Display name
 */
export const getTemplateDisplayName = (template) => {
  return template.name || "Untitled Template";
};

/**
 * Gets template summary statistics
 * @param {Template} template - Template object
 * @returns {Object} Template statistics
 */
export const getTemplateStats = (template) => {
  const totalItems = template.items.length;
  const requiredItems = template.items.filter((item) => item.required).length;
  const itemTypes = [...new Set(template.items.map((item) => item.type))];

  return {
    totalItems,
    requiredItems,
    optionalItems: totalItems - requiredItems,
    itemTypes: itemTypes.length,
    estimatedTime: template.metadata.estimatedTimeMinutes,
    version: template.version,
  };
};

/**
 * Formats template for display in UI components
 * @param {Template} template - Template object
 * @returns {Object} Formatted template data
 */
export const formatTemplateForDisplay = (template) => {
  const stats = getTemplateStats(template);

  return {
    id: template.id,
    name: getTemplateDisplayName(template),
    description: template.description,
    version: template.version,
    isActive: template.isActive,
    category: template.metadata.category || "",
    tags: template.metadata.tags || "",
    stats,
    createdAt: template.createdAt,
    updatedAt: template.updatedAt,
  };
};

/**
 * Searches templates by name, description, or tags
 * @param {Template[]} templates - Array of templates to search
 * @param {string} query - Search query
 * @returns {Template[]} Filtered templates
 */
export const searchTemplates = (templates, query) => {
  if (!query || query.trim().length === 0) {
    return templates;
  }

  const searchTerm = query.toLowerCase().trim();

  return templates.filter((template) => {
    const name = template.name.toLowerCase();
    const description = template.description.toLowerCase();
    const tags = template.metadata.tags
      .map((tag) => tag.toLowerCase())
      .join(" ");
    const category = template.metadata.category.toLowerCase();

    return (
      name.includes(searchTerm) ||
      description.includes(searchTerm) ||
      tags.includes(searchTerm) ||
      category.includes(searchTerm)
    );
  });
};

/**
 * Filters templates by category
 * @param {Template[]} templates - Array of templates to filter
 * @param {string} category - Category to filter by
 * @returns {Template[]} Filtered templates
 */
export const filterTemplatesByCategory = (templates, category) => {
  if (!category) {
    return templates;
  }

  return templates.filter(
    (template) => template.metadata.category === category
  );
};

/**
 * Gets all unique categories from templates
 * @param {Template[]} templates - Array of templates
 * @returns {string[]} Array of unique categories
 */
export const getTemplateCategories = (templates) => {
  const categories = templates.map((template) => template.metadata.category);
  return [...new Set(categories)].sort();
};

// Utility functions
// const isValidVersion = (version) => {
//   const versionRegex = /^\d+\.\d+\.\d+$/;
//   return versionRegex.test(version);
// };
const isValidVersion = (version) => {
  const intVersion = parseInt(version);
  return !isNaN(intVersion);
};

// const incrementVersion = (currentVersion) => {
//   const parts = currentVersion.split(".");
//   const patch = parseInt(parts[2]) + 1;
//   return `${parts[0]}.${parts[1]}.${patch}`;
// };

/**
 * Template factory functions
 */
export const TemplateFactory = {
  /**
   * Creates a basic template with common item types
   * @param {string} name - Template name
   * @param {string} description - Template description
   * @returns {Template} Basic template
   */
  createBasic: (name, description = "") => {
    return createTemplate({
      name,
      description,
      items: [
        createTemplateItem(ITEM_TYPES.CHECKBOX, {
          label: "Task completed",
          required: true,
        }),
        createTemplateItem(ITEM_TYPES.DATE_TEXT, {
          label: "Completion date and notes",
          required: false,
        }),
      ],
    });
  },

  /**
   * Creates an inspection template
   * @param {string} name - Template name
   * @returns {Template} Inspection template
   */
  createInspection: (name) => {
    return createTemplate({
      name,
      description: "Standard inspection checklist template",
      metadata: {
        category: "inspection",
        tags: ["inspection", "quality", "safety"],
        estimatedTimeMinutes: 20,
      },
      items: [
        createTemplateItem(ITEM_TYPES.CHECKBOX, {
          label: "Visual inspection completed",
          required: true,
        }),
        createTemplateItem(ITEM_TYPES.CHECKBOX, {
          label: "Safety requirements met",
          required: true,
        }),
        createTemplateItem(ITEM_TYPES.DATE_TEXT, {
          label: "Inspection date and findings",
          required: true,
        }),
        createTemplateItem(ITEM_TYPES.TEXT, {
          label: "Additional comments",
          required: false,
        }),
      ],
    });
  },

  /**
   * Creates a maintenance template
   * @param {string} name - Template name
   * @returns {Template} Maintenance template
   */
  createMaintenance: (name) => {
    return createTemplate({
      name,
      description: "Standard maintenance checklist template",
      metadata: {
        category: "maintenance",
        tags: ["maintenance", "equipment", "service"],
        estimatedTimeMinutes: 30,
      },
      items: [
        createTemplateItem(ITEM_TYPES.CHECKBOX, {
          label: "Equipment cleaned",
          required: true,
        }),
        createTemplateItem(ITEM_TYPES.CHECKBOX, {
          label: "Parts inspected",
          required: true,
        }),
        createTemplateItem(ITEM_TYPES.CHECKBOX, {
          label: "Lubrication applied",
          required: false,
        }),
        createTemplateItem(ITEM_TYPES.DATE_TEXT, {
          label: "Service date and notes",
          required: true,
        }),
      ],
    });
  },
};

export default {
  createTemplate,
  validateTemplate,
  updateTemplate,
  addTemplateItem,
  removeTemplateItem,
  reorderTemplateItem,
  duplicateTemplate,
  getTemplateDisplayName,
  getTemplateStats,
  formatTemplateForDisplay,
  searchTemplates,
  filterTemplatesByCategory,
  getTemplateCategories,
  TemplateFactory,
};
